import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, LogOut, RefreshCcw, Trash2, ExternalLink, Mail, Phone, Building2, Search, ArchiveRestore, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "../lib/adminAuth";
import BrandMark from "../components/BrandMark";

export default function AdminInquiries() {
  const { user, loading, logout, authedAxios } = useAdminAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, archived: 0 });
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(true);
  const [selected, setSelected] = useState(null);

  const api = useMemo(() => authedAxios(), [authedAxios]);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const [list, st] = await Promise.all([
        api.get("/admin/inquiries", { params }),
        api.get("/admin/stats"),
      ]);
      setInquiries(list.data);
      setStats(st.data);
    } catch (e) {
      toast.error("Failed to load inquiries");
    } finally {
      setBusy(false);
    }
  }, [api, filter]);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/admin" replace />;

  const filtered = inquiries.filter((q) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      q.name?.toLowerCase().includes(s) ||
      q.email?.toLowerCase().includes(s) ||
      q.organisation?.toLowerCase().includes(s) ||
      q.message?.toLowerCase().includes(s)
    );
  });

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/inquiries/${id}`, { status });
      setInquiries((arr) => arr.map((x) => (x.id === id ? data : x)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Marked ${status}`);
      // refresh stats
      api.get("/admin/stats").then((r) => setStats(r.data));
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      setInquiries((arr) => arr.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
      api.get("/admin/stats").then((r) => setStats(r.data));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const exportCSV = () => {
    const headers = ["id", "created_at", "name", "organisation", "email", "phone", "inquiry_type", "status", "message"];
    const rows = inquiries.map((q) => headers.map((h) => csvEscape(q[h] ?? "")).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bjc-inquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[var(--bjc-bg)]" data-testid="admin-inquiries-page">
      {/* Top bar */}
      <header className="bjc-header sticky top-0 z-40">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <BrandMark height={64} />
              <div className="hidden sm:flex flex-col leading-tight border-l border-[var(--bjc-border)] pl-4">
                <span className="font-serif-display text-lg text-[var(--bjc-text)]">Admin</span>
                <span className="text-[10px] tracking-[0.28em] uppercase text-[var(--bjc-gold)] mt-0.5">Inquiries Console</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/" data-testid="admin-view-site" className="bjc-link hidden md:inline-flex">
                View Site <ExternalLink size={12} />
              </Link>
              <button
                type="button"
                onClick={refresh}
                data-testid="admin-refresh"
                className="bjc-btn-secondary !py-3 !px-4"
                title="Refresh"
              >
                <RefreshCcw size={14} />
              </button>
              <button
                type="button"
                onClick={() => { logout(); navigate("/admin"); }}
                data-testid="admin-logout"
                className="bjc-btn-secondary !py-3 !px-5"
              >
                <LogOut size={14} /> Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--bjc-border)] border border-[var(--bjc-border)] mb-10" data-testid="admin-stats">
          {[
            { k: "Total", v: stats.total, key: "all" },
            { k: "New", v: stats.new, key: "new" },
            { k: "Read", v: stats.read, key: "read" },
            { k: "Archived", v: stats.archived, key: "archived" },
          ].map((s) => (
            <button
              key={s.k}
              type="button"
              onClick={() => setFilter(s.key)}
              data-testid={`stat-${s.key}`}
              className={`bg-[var(--bjc-bg)] hover:bg-[var(--bjc-surface)] transition-colors p-6 md:p-8 text-left ${filter === s.key ? "ring-1 ring-[var(--bjc-gold)] bg-[var(--bjc-surface)]" : ""}`}
            >
              <div className="bjc-overline">{s.k}</div>
              <div className="mt-3 font-serif-display text-3xl md:text-5xl text-[var(--bjc-text)]">{s.v}</div>
            </button>
          ))}
        </section>

        {/* Search + actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[var(--bjc-text-faint)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search"
              placeholder="Search name, email, organisation, message..."
              className="bjc-input pl-6"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[var(--bjc-text-muted)]">
              {filtered.length} {filtered.length === 1 ? "inquiry" : "inquiries"}
            </span>
            <button
              type="button"
              onClick={exportCSV}
              data-testid="admin-export-csv"
              className="bjc-btn-secondary !py-3 !px-5"
              disabled={inquiries.length === 0}
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* List */}
          <section className="lg:col-span-7 xl:col-span-8 border border-[var(--bjc-border)]">
            {busy ? (
              <div className="p-16 text-center text-[var(--bjc-text-muted)]">
                <Loader2 size={20} className="animate-spin inline mr-2" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-16 text-center" data-testid="admin-empty">
                <span className="bjc-overline">— No inquiries</span>
                <p className="mt-4 text-[var(--bjc-text-muted)]">Nothing matches the current filter.</p>
              </div>
            ) : (
              <ul className="divide-y divide-[var(--bjc-border)]">
                {filtered.map((q) => (
                  <li
                    key={q.id}
                    onClick={() => setSelected(q)}
                    data-testid={`inquiry-row-${q.id}`}
                    className={`p-6 md:p-8 cursor-pointer hover:bg-[var(--bjc-surface)] transition-colors ${selected?.id === q.id ? "bg-[var(--bjc-surface)]" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <StatusPill status={q.status} />
                          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--bjc-text-faint)]">
                            {formatDate(q.created_at)}
                          </span>
                        </div>
                        <h3 className="font-serif-display text-xl md:text-2xl text-[var(--bjc-text)] truncate">
                          {q.name}{q.organisation ? <span className="text-[var(--bjc-text-muted)]"> · {q.organisation}</span> : null}
                        </h3>
                        <p className="mt-2 text-sm text-[var(--bjc-text-muted)] line-clamp-2">{q.message}</p>
                        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[var(--bjc-text-faint)]">
                          <span className="flex items-center gap-1.5"><Mail size={11} /> {q.email}</span>
                          {q.phone && <span className="flex items-center gap-1.5"><Phone size={11} /> {q.phone}</span>}
                          <span className="uppercase tracking-[0.16em]">{q.inquiry_type}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Detail */}
          <section className="lg:col-span-5 xl:col-span-4">
            <div className="border border-[var(--bjc-border)] sticky top-24" data-testid="admin-detail">
              {!selected ? (
                <div className="p-10 text-center">
                  <span className="bjc-overline">— Detail</span>
                  <p className="mt-4 text-sm text-[var(--bjc-text-muted)]">Select an inquiry to view details and actions.</p>
                </div>
              ) : (
                <div className="p-8">
                  <StatusPill status={selected.status} />
                  <h2 className="mt-4 font-serif-display text-2xl md:text-3xl text-[var(--bjc-text)] leading-tight">
                    {selected.name}
                  </h2>
                  <div className="mt-4 space-y-2 text-sm text-[var(--bjc-text-muted)]">
                    {selected.organisation && (
                      <div className="flex items-center gap-2"><Building2 size={12} /> {selected.organisation}</div>
                    )}
                    <a href={`mailto:${selected.email}`} className="flex items-center gap-2 hover:text-[var(--bjc-gold)] transition-colors">
                      <Mail size={12} /> {selected.email}
                    </a>
                    {selected.phone && (
                      <a href={`tel:${selected.phone}`} className="flex items-center gap-2 hover:text-[var(--bjc-gold)] transition-colors">
                        <Phone size={12} /> {selected.phone}
                      </a>
                    )}
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bjc-text-faint)] pt-2">
                      {formatDate(selected.created_at)} · {selected.inquiry_type}
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-[var(--bjc-border)]">
                    <div className="bjc-overline mb-3">Message</div>
                    <p className="text-[var(--bjc-text)] leading-relaxed whitespace-pre-wrap text-sm">
                      {selected.message}
                    </p>
                  </div>

                  <div className="mt-8 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setStatus(selected.id, "read")}
                      data-testid="admin-mark-read"
                      className="bjc-btn-secondary !py-3 !px-3 text-xs"
                      disabled={selected.status === "read"}
                    >
                      <CheckCheck size={12} /> Mark Read
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatus(selected.id, "archived")}
                      data-testid="admin-archive"
                      className="bjc-btn-secondary !py-3 !px-3 text-xs"
                      disabled={selected.status === "archived"}
                    >
                      <ArchiveRestore size={12} /> Archive
                    </button>
                    <a
                      href={`mailto:${selected.email}?subject=Re: Your inquiry — Bharat Jeswani Consulting`}
                      data-testid="admin-reply"
                      className="bjc-btn-primary !py-3 !px-3 text-xs justify-center"
                    >
                      Reply by Email
                    </a>
                    <button
                      type="button"
                      onClick={() => remove(selected.id)}
                      data-testid="admin-delete"
                      className="!py-3 !px-3 text-xs inline-flex items-center justify-center gap-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors uppercase tracking-[0.18em]"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: { label: "New", cls: "border-[var(--bjc-gold)]/60 text-[var(--bjc-gold)]" },
    read: { label: "Read", cls: "border-white/20 text-[var(--bjc-text-muted)]" },
    archived: { label: "Archived", cls: "border-white/10 text-[var(--bjc-text-faint)]" },
  };
  const v = map[status] || map.new;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 border text-[10px] tracking-[0.18em] uppercase ${v.cls}`}>
      {v.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function csvEscape(v) {
  const s = String(v ?? "").replace(/"/g, '""').replace(/\r?\n/g, " ");
  return `"${s}"`;
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bjc-bg)]">
      <Loader2 size={20} className="animate-spin text-[var(--bjc-gold)]" />
    </div>
  );
}
